import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { X, Save, FlaskConical, Loader2, Plus, Trash2, CheckCircle2 } from 'lucide-react'
import {
  createCustomTool,
  updateCustomTool,
  testCustomTool,
  fetchCustomTools,
} from '../../../features/openai/openaiSlice'
import { toast } from 'react-toastify'
import {
  customToolFormInputClass,
  customToolFormLabelClass,
  customToolFormSelectClass,
  useCustomToolAuthFields,
} from './customToolShared'

function slugifyToolName(raw: string): string {
  let s = raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  if (!/^[a-z]/.test(s)) s = `x${s}`
  return (s || 'tool').slice(0, 100)
}

const PARAMS_PLACEHOLDER = `{
  "type": "object",
  "properties": {
    "query": { "type": "object", "additionalProperties": true },
    "path": { "type": "object", "additionalProperties": true },
    "body": { "type": "object" }
  }
}`

export interface CustomToolFormProps {
  onClose: () => void
  editing: Record<string, any> | null
  /** When true, omit top title bar (use parent Modal.Header). */
  hideChrome?: boolean
}

const CustomToolForm: React.FC<CustomToolFormProps> = ({ onClose, editing, hideChrome = false }) => {
  const dispatch = useDispatch()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [endpointUrl, setEndpointUrl] = useState('')
  const [httpMethod, setHttpMethod] = useState('GET')
  const [authType, setAuthType] = useState('none')
  const [authConfig, setAuthConfig] = useState<Record<string, string>>({})
  const [paramsText, setParamsText] = useState('')
  const [headerRows, setHeaderRows] = useState<{ key: string; value: string }[]>([{ key: '', value: '' }])
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const { authField } = useCustomToolAuthFields(authConfig, setAuthConfig)

  useEffect(() => {
    if (editing) {
      setName(editing.name || '')
      setDescription(editing.description || '')
      setEndpointUrl(editing.endpoint_url || '')
      setHttpMethod((editing.http_method || 'GET').toUpperCase())
      setAuthType(editing.auth_type || 'none')
      const ac = editing.auth_config || {}
      setAuthConfig({
        key_name: ac.key_name || '', key_value: ac.key_value || '', in: ac.in || 'header',
        token: ac.token || '', username: ac.username || '', password: ac.password || '',
        token_url: ac.token_url || '', client_id: ac.client_id || '',
        client_secret: ac.client_secret || '', scope: ac.scope || '',
      })
      setParamsText(editing.parameters_schema ? JSON.stringify(editing.parameters_schema, null, 2) : '')
      const h = editing.headers && typeof editing.headers === 'object' ? editing.headers : {}
      const pairs = Object.entries(h).map(([key, value]) => ({ key, value: String(value) }))
      setHeaderRows(pairs.length ? pairs : [{ key: '', value: '' }])
    } else {
      setName(''); setDescription(''); setEndpointUrl(''); setHttpMethod('GET')
      setAuthType('none'); setAuthConfig({}); setParamsText('')
      setHeaderRows([{ key: '', value: '' }])
    }
  }, [editing])

  const buildHeadersObject = () => {
    const o: Record<string, string> = {}
    for (const r of headerRows) { if (r.key.trim()) o[r.key.trim()] = r.value }
    return Object.keys(o).length ? o : null
  }

  const buildAuthBody = () => {
    switch (authType) {
      case 'api_key': return { key_name: authConfig.key_name, key_value: authConfig.key_value, in: authConfig.in === 'query' ? 'query' : 'header' }
      case 'bearer': return { token: authConfig.token }
      case 'basic': return { username: authConfig.username, password: authConfig.password }
      case 'oauth2_client_credentials': return { token_url: authConfig.token_url, client_id: authConfig.client_id, client_secret: authConfig.client_secret, scope: authConfig.scope || '' }
      default: return {}
    }
  }

  const handleValidateJson = () => {
    const t = paramsText.trim()
    if (!t) { toast.success('Empty schema is OK.'); return }
    try { JSON.parse(t); toast.success('Valid JSON.') } catch (e: any) { toast.error(`Invalid JSON: ${e.message}`) }
  }

  const handleSave = async () => {
    let parameters_schema: any = null
    const pt = paramsText.trim()
    if (pt) {
      try { parameters_schema = JSON.parse(pt) } catch (e: any) { toast.error(`Parameters JSON invalid: ${e.message}`); return }
    }
    const body: any = {
      name: slugifyToolName(name || 'tool'), description: description.slice(0, 500),
      endpoint_url: endpointUrl.trim(), http_method: httpMethod, auth_type: authType,
      parameters_schema, headers: buildHeadersObject(),
      is_enabled: editing ? editing.is_enabled !== false : true,
    }
    const rawAuth = buildAuthBody()
    if (editing) {
      const ac: Record<string, string> = { ...rawAuth }
      for (const k of ['key_value', 'token', 'password', 'client_secret']) { if (!ac[k]) delete ac[k] }
      body.auth_config = ac
    } else {
      body.auth_config = rawAuth
    }
    setSaving(true)
    try {
      if (editing) {
        await dispatch(updateCustomTool({ toolId: editing.id, body }) as any).unwrap()
        toast.success('Tool updated')
      } else {
        await dispatch(createCustomTool(body) as any).unwrap()
        toast.success('Tool created')
      }
      await dispatch(fetchCustomTools() as any)
      onClose()
    } catch (err: any) {
      toast.error(err?.error || err?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    if (!editing?.id) { toast.info('Save the tool first, then test.'); return }
    setTesting(true)
    try {
      const res = await dispatch(testCustomTool({ toolId: editing.id, args: {} }) as any).unwrap()
      if (res.ok) toast.success(`Test OK — HTTP ${res.status_code}`)
      else toast.error(`Test failed — ${res.error || res.status_code}`)
    } catch (err: any) {
      toast.error(err?.error || 'Test request failed')
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-bg-white">
      {!hideChrome && (
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-shrink-0">
          <h2 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-m-0">
            {editing ? 'Edit tool' : 'New tool'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="tw-p-1.5 tw-rounded-lg tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer"
          >
            <X className="tw-w-4 tw-h-4" />
          </button>
        </div>
      )}

      {/* Scrollable body */}
      <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-px-5 tw-py-5 tw-space-y-5">
        <div>
          <label className={customToolFormLabelClass}>Name</label>
          <input className={customToolFormInputClass} value={name} onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} placeholder="my_api_lookup" />
          <p className="tw-text-[11px] tw-text-gray-400 tw-mt-1">snake_case, letters & numbers only</p>
        </div>

        <div>
          <label className={customToolFormLabelClass}>Description</label>
          <textarea className={`${customToolFormInputClass} tw-resize-none`} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe when the agent should use this tool." maxLength={500} />
        </div>

        <div className="tw-grid tw-grid-cols-[1fr_auto] tw-gap-2">
          <div>
            <label className={customToolFormLabelClass}>Endpoint URL</label>
            <input className={customToolFormInputClass} value={endpointUrl} onChange={(e) => setEndpointUrl(e.target.value)} placeholder="https://api.example.com/v1/resource" />
          </div>
          <div>
            <label className={customToolFormLabelClass}>Method</label>
            <select className={customToolFormSelectClass} value={httpMethod} onChange={(e) => setHttpMethod(e.target.value)}>
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={customToolFormLabelClass}>Authentication</label>
          <select className={customToolFormSelectClass} value={authType} onChange={(e) => setAuthType(e.target.value)}>
            <option value="none">None</option>
            <option value="api_key">API Key</option>
            <option value="bearer">Bearer Token</option>
            <option value="basic">Basic Auth</option>
            <option value="oauth2_client_credentials">OAuth2 Client Credentials</option>
          </select>
          {authType === 'api_key' && (
            <div className="tw-mt-2 tw-space-y-2 tw-pl-3 tw-border-l-2 tw-border-blue-200">
              <input className={customToolFormInputClass} placeholder="Key name (e.g. X-Api-Key)" value={authConfig.key_name || ''} onChange={(e) => setAuthConfig((c) => ({ ...c, key_name: e.target.value }))} />
              <input className={customToolFormInputClass} type="text" placeholder="Key value" value={authConfig.key_value || ''} onChange={(e) => setAuthConfig((c) => ({ ...c, key_value: e.target.value }))} />
              <select className={customToolFormSelectClass} value={authConfig.in || 'header'} onChange={(e) => setAuthConfig((c) => ({ ...c, in: e.target.value }))}>
                <option value="header">Send in Header</option>
                <option value="query">Send in Query</option>
              </select>
            </div>
          )}
          {authType === 'bearer' && <div className="tw-mt-2 tw-pl-3 tw-border-l-2 tw-border-blue-200">{authField('Bearer token', 'token', 'text')}</div>}
          {authType === 'basic' && <div className="tw-mt-2 tw-space-y-2 tw-pl-3 tw-border-l-2 tw-border-blue-200">{authField('Username', 'username')}{authField('Password', 'password', 'text')}</div>}
          {authType === 'oauth2_client_credentials' && (
            <div className="tw-mt-2 tw-space-y-2 tw-pl-3 tw-border-l-2 tw-border-blue-200">
              {authField('Token URL', 'token_url')}{authField('Client ID', 'client_id')}{authField('Client Secret', 'client_secret', 'text')}{authField('Scope (optional)', 'scope')}
            </div>
          )}
        </div>

        <div>
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
            <label className={`${customToolFormLabelClass} tw-mb-0`}>Parameters (JSON Schema)</label>
            <button type="button" onClick={handleValidateJson} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-medium tw-text-blue-600 hover:tw-text-blue-700 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-px-2 tw-py-1 tw-rounded-md hover:tw-bg-blue-50 tw-transition-colors">
              <CheckCircle2 className="tw-w-3 tw-h-3" /> Validate
            </button>
          </div>
          <textarea className={`${customToolFormInputClass} tw-font-mono tw-text-xs tw-resize-y`} rows={7} value={paramsText} onChange={(e) => setParamsText(e.target.value)} placeholder={PARAMS_PLACEHOLDER} />
        </div>

        <div>
          <label className={customToolFormLabelClass}>Custom Headers</label>
          <div className="tw-space-y-2">
            {headerRows.map((row, i) => (
              <div key={i} className="tw-flex tw-gap-2 tw-items-center">
                <input className={`${customToolFormInputClass} tw-flex-1`} placeholder="Header name" value={row.key} onChange={(e) => { const next = [...headerRows]; next[i] = { ...next[i], key: e.target.value }; setHeaderRows(next) }} />
                <input className={`${customToolFormInputClass} tw-flex-1`} placeholder="Value" value={row.value} onChange={(e) => { const next = [...headerRows]; next[i] = { ...next[i], value: e.target.value }; setHeaderRows(next) }} />
                <button type="button" onClick={() => setHeaderRows(headerRows.filter((_, j) => j !== i))} className="tw-p-1.5 tw-rounded-lg tw-text-gray-400 hover:tw-text-red-500 hover:tw-bg-red-50 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer tw-flex-shrink-0">
                  <Trash2 className="tw-w-3.5 tw-h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setHeaderRows([...headerRows, { key: '', value: '' }])} className="tw-inline-flex tw-items-center tw-gap-1 tw-mt-2 tw-text-xs tw-font-medium tw-text-gray-500 hover:tw-text-gray-700 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-px-2 tw-py-1 tw-rounded-md hover:tw-bg-gray-100 tw-transition-colors">
            <Plus className="tw-w-3 tw-h-3" /> Add header
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-4 tw-border-t tw-border-gray-100 tw-shrink-0 tw-bg-gray-50/50">
        <button onClick={handleSave} disabled={saving} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-shadow-sm hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors">
          {saving ? <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" /> : <Save className="tw-w-3.5 tw-h-3.5" />}
          {saving ? 'Saving...' : 'Save'}
        </button>
        {editing && (
          <button onClick={handleTest} disabled={testing} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-bg-white tw-text-gray-700 tw-text-sm tw-font-medium tw-border tw-border-gray-200 tw-cursor-pointer tw-shadow-sm hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors">
            {testing ? <Loader2 className="tw-w-3.5 tw-h-3.5 tw-animate-spin" /> : <FlaskConical className="tw-w-3.5 tw-h-3.5" />}
            Test
          </button>
        )}
        <button onClick={onClose} className="tw-ml-auto tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors">
          Cancel
        </button>
      </div>
    </div>
  )
}

export default CustomToolForm
